/**
 * ============================================
 * RANGE DATA RE-AGGREGATION (Phase A1 date-range filter)
 * ============================================
 *
 * Given the per-day additive rollups the backend now ships (`*ByDay`), rebuild
 * the range-restricted equivalents of the all-time dashboard fields the charts
 * already consume — so a date range makes the corresponding cards respond with
 * NO backend recompute. The formulas mirror the backend finalize helpers
 * (analytics_processor.py) exactly so all-time and full-range agree.
 *
 * ONLY additive metrics are covered here. Unique-player counts, geo, level
 * entries/quit, onboarding funnel, churn and retention are NOT re-aggregated
 * (they aren't stored per-day) and stay all-time. See DATE_RANGE_FILTER_PLAN.md.
 */

import type { AnalyticsData } from '@/hooks/useAnalytics';

type Accum = { sum: number; count: number };
type InRange = (dateStr: string | undefined | null) => boolean;

/** Sum {sum,count} accumulators across the in-range days, keyed by category. */
function sumAccum(
  byDay: Record<string, Record<string, Accum>> | undefined,
  inRange: InRange
): Record<string, Accum> {
  const out: Record<string, Accum> = {};
  for (const [date, cats] of Object.entries(byDay || {})) {
    if (!inRange(date)) continue;
    for (const [cat, sc] of Object.entries(cats)) {
      const o = out[cat] || (out[cat] = { sum: 0, count: 0 });
      o.sum += sc?.sum || 0;
      o.count += sc?.count || 0;
    }
  }
  return out;
}

/** Sum flat counters across the in-range days, keyed by key. */
function sumCounts(
  byDay: Record<string, Record<string, number>> | undefined,
  inRange: InRange
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [date, m] of Object.entries(byDay || {})) {
    if (!inRange(date)) continue;
    for (const [k, v] of Object.entries(m)) out[k] = (out[k] || 0) + (Number(v) || 0);
  }
  return out;
}

/** Sum 2-level nested counters across the in-range days. */
function sumNested(
  byDay: Record<string, Record<string, Record<string, number>>> | undefined,
  inRange: InRange
): Record<string, Record<string, number>> {
  const out: Record<string, Record<string, number>> = {};
  for (const [date, outer] of Object.entries(byDay || {})) {
    if (!inRange(date)) continue;
    for (const [k1, inner] of Object.entries(outer)) {
      const o = out[k1] || (out[k1] = {});
      for (const [k2, v] of Object.entries(inner)) o[k2] = (o[k2] || 0) + (Number(v) || 0);
    }
  }
  return out;
}

/** round(x, 1) — matches Python's rounding closely enough for display. */
const r1 = (x: number) => Math.round(x * 10) / 10;

/**
 * Range-aware campaignProgress + levelStats from the per-day per-level EVENT
 * counts. This is ATTEMPT-based (completions / entries over the window), unlike
 * the all-time version which counts unique devices — a completion rate here is
 * "% of level attempts in range that finished", not "% of unique players". A
 * player who replays a level counts each attempt. See DATE_RANGE_FILTER_PLAN.md.
 */
function buildLevelProgress(data: AnalyticsData, inRange: InRange) {
  const entries = sumCounts(data.levelEntryCountsByDay, inRange);
  const completions = sumCounts(data.levelCompletionCountsByDay, inRange);
  const restarts = sumCounts(data.levelRestartCountsByDay, inRange);
  const times = sumAccum(data.levelTimesByDay, inRange);

  const campaignProgress: AnalyticsData['campaignProgress'] = [];
  const levelStats: AnalyticsData['levelStats'] = [];

  // Iterate levels that had at least one entry event in range — mirrors the
  // all-time path, which iterates level_entry_devices keys.
  for (const level of Object.keys(entries)) {
    const e = entries[level] || 0;
    if (e <= 0) continue;
    const c = completions[level] || 0;
    const r = restarts[level] || 0;
    // Clamp: an out-of-range entry with an in-range completion could push c>e.
    const completion = Math.min((c / e) * 100, 100);
    const quitRate = (Math.max(e - c, 0) / e) * 100;
    const restartRate = (r / e) * 100;
    const t = times[level];
    const avgTime = t && t.count ? t.sum / t.count / 60 : 0;

    campaignProgress.push({
      level,
      completion: r1(completion),
      quitRate: r1(quitRate),
      restartRate: r1(restartRate),
    });
    levelStats.push({
      level,
      avgTime: t && t.count ? r1(avgTime) : null,
      quitRate: r1(quitRate),
      entries: e,
      completions: c,
      restarts: r,
    });
  }

  return { campaignProgress, levelStats };
}
/** avg seconds -> minutes, 1dp (backend calc_avg / avg_mins). */
const avgMins = (a: Accum | undefined) => (a && a.count ? r1(a.sum / a.count / 60) : 0);
// Levels behind the Local PvP / Local Co-op bars of "Where Players Spend Time".
const LOCAL_PVP_LEVELS = ['PvP Lava', 'PvP Jungle', 'PvP Night', 'PvP Monastery', 'Labs_CarRace', 'Labs_RocketPodRace', 'car_race', 'rocketpod_race'];
const LOCAL_COOP_LEVELS = ['Labs_Coop_Robo'];

/**
 * Adds the Local PvP / Local Co-op bars after Labs. They come from the per-level
 * times, so `source` is the (country-filtered) data still holding them; `view`
 * is what's on screen (all-time or range-derived).
 */
export function withLocalModeTimes<T extends AnalyticsData | null>(view: T, source: AnalyticsData | null, inRange: InRange): T {
  const dist = view?.globalTimeDistribution;
  if (!view || !dist || !source?.levelTimesByDay) return view;
  const times = sumAccum(source.levelTimesByDay, inRange);
  const row = (area: string, levels: string[]) => {
    const a = levels.reduce((acc, l) => ({
      sum: acc.sum + (times[l]?.sum || 0), count: acc.count + (times[l]?.count || 0),
    }), { sum: 0, count: 0 });
    return { area, avgTime: avgMins(a), sessions: a.count };
  };
  const distribution = [...dist.distribution];
  distribution.splice(distribution.findIndex(d => d.area === 'Labs') + 1, 0,
    row('Local PvP', LOCAL_PVP_LEVELS), row('Local Co-op', LOCAL_COOP_LEVELS));
  return { ...view, globalTimeDistribution: { ...dist, distribution } };
}

/** total seconds -> "Xh Ym" / "Ym" (backend format_time). */
function fmtTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * Return a shallow-cloned AnalyticsData with the additive-derived fields
 * recomputed for the selected range. When `isAllTime`, returns `data`
 * unchanged. All-time-only fields are left as-is on the clone.
 */
export function deriveRangeData(
  data: AnalyticsData | null,
  inRange: InRange,
  isAllTime: boolean
): AnalyticsData | null {
  if (!data || isAllTime) return data;

  const g = sumAccum(data.globalTimeAccumByDay, inRange);
  const sess = (c: string) => g[c]?.count || 0;

  // --- globalTimeDistribution (Where Players Spend Time) ---
  const globalTimeDistribution = {
    distribution: [
      { area: 'Single Player', avgTime: avgMins(g['singleplayer']), sessions: sess('singleplayer') },
      { area: 'Labs', avgTime: avgMins(g['labz']), sessions: sess('labz') },
      { area: 'Level Editor', avgTime: avgMins(g['level_editor']), sessions: sess('level_editor') },
      { area: '2D Editor', avgTime: avgMins(g['editor_2d']), sessions: sess('editor_2d') },
      { area: 'Track Editor', avgTime: avgMins(g['track_editor']), sessions: sess('track_editor') },
      { area: 'Avatar Editor', avgTime: avgMins(g['avatar_editor']), sessions: sess('avatar_editor') },
      { area: 'NPC Editor', avgTime: avgMins(g['npc_editor']), sessions: sess('npc_editor') },
      { area: 'Chassis Editor', avgTime: avgMins(g['chassis_editor']), sessions: sess('chassis_editor') },
      { area: 'Coop Level Editor', avgTime: avgMins(g['coop_level_editor']), sessions: sess('coop_level_editor') },
    ],
    totalSinglePlayerTime: g['singleplayer']?.sum ? r1(g['singleplayer'].sum / 60) : 0,
    totalLabzTime: g['labz']?.sum ? r1(g['labz'].sum / 60) : 0,
    totalUgcTime: g['ugc']?.sum ? r1(g['ugc'].sum / 60) : 0,
  };

  // --- timeExpenditure (editor avg session time) ---
  // editor_time_accum == global_time_accum for editor keys (incremented
  // together in the processor), so we derive both from the same rollup.
  const timeExpenditure = {
    avatarEditor: avgMins(g['avatar_editor']), npcEditor: avgMins(g['npc_editor']),
    levelEditor: avgMins(g['level_editor']), editor2d: avgMins(g['editor_2d']),
    trackEditor: avgMins(g['track_editor']), chassisEditor: avgMins(g['chassis_editor']),
    coopLevelEditor: avgMins(g['coop_level_editor']),
    avatarEditorSamples: sess('avatar_editor'), npcEditorSamples: sess('npc_editor'),
    levelEditorSamples: sess('level_editor'), editor2dSamples: sess('editor_2d'),
    trackEditorSamples: sess('track_editor'), chassisEditorSamples: sess('chassis_editor'),
    coopLevelEditorSamples: sess('coop_level_editor'),
  };

  // --- editorGrossStats (gross totals + opens + saves) ---
  const gs = sumCounts(data.editorGrossSecondsByDay, inRange);
  const opens = sumCounts(data.dailyEditorOpens, inRange); // dailyEditorOpens is per-day already
  const saves = sumCounts(data.levelSaveCountsByDay, inRange);
  const sec = (k: string) => gs[k] || 0;
  const editorGrossStats = {
    ...(data.editorGrossStats as any), // keep avatarEditorHourly (last-24h) etc.
    levelEditorTotalSeconds: sec('level_editor'), levelEditorTotalTime: fmtTime(sec('level_editor')),
    npcEditorTotalSeconds: sec('npc_editor'), npcEditorTotalTime: fmtTime(sec('npc_editor')),
    avatarEditorTotalSeconds: sec('avatar_editor'), avatarEditorTotalTime: fmtTime(sec('avatar_editor')),
    editor2dTotalSeconds: sec('editor_2d'), editor2dTotalTime: fmtTime(sec('editor_2d')),
    trackEditorTotalSeconds: sec('track_editor'), trackEditorTotalTime: fmtTime(sec('track_editor')),
    chassisEditorTotalSeconds: sec('chassis_editor'), chassisEditorTotalTime: fmtTime(sec('chassis_editor')),
    coopLevelEditorTotalSeconds: sec('coop_level_editor'), coopLevelEditorTotalTime: fmtTime(sec('coop_level_editor')),
    levelEditorOpens: opens['level_editor'] || 0, avatarEditorOpens: opens['avatar_editor'] || 0,
    npcEditorOpens: opens['npc_editor'] || 0, editor2dOpens: opens['editor_2d'] || 0,
    trackEditorOpens: opens['track_editor'] || 0, chassisEditorOpens: opens['chassis_editor'] || 0,
    coopLevelEditorOpens: opens['coop_level_editor'] || 0,
    levelSaves: saves['uploaded_3d'] || 0, levelPublishes: saves['published_3d'] || 0,
    level2dSaves: saves['uploaded_2d'] || 0, level2dPublishes: saves['published_2d'] || 0,
    trackEditorSaves: saves['uploaded_track'] || 0, trackEditorPublishes: saves['published_track'] || 0,
    coopLevelEditorSaves: saves['uploaded_coop'] || 0, coopLevelEditorPublishes: saves['published_coop'] || 0,
  };

  // --- characterStats ---
  const cc = sumCounts(data.characterCountsByDay, inRange);
  const charTotal = Object.values(cc).reduce((a, b) => a + b, 0);
  const characterStats = charTotal > 0
    ? {
        characters: Object.entries(cc)
          .sort((a, b) => b[1] - a[1])
          .map(([id, count]) => ({
            character: id.charAt(0).toUpperCase() + id.slice(1),
            count,
            percentage: r1((count / charTotal) * 100),
          })),
        totalSelections: charTotal,
      }
    : { characters: [] };

  const { campaignProgress, levelStats } = buildLevelProgress(data, inRange);

  return {
    ...data,
    globalTimeDistribution,
    timeExpenditure,
    editorGrossStats,
    characterStats,
    campaignProgress,
    levelStats,
    mainMenuButtonCounts: sumCounts(data.mainMenuButtonCountsByDay, inRange),
    multiplierPickedCounts: sumNested(data.multiplierPickedCountsByDay, inRange),
    levelItemsCollected: sumNested(data.levelItemsCollectedByDay, inRange),
  };
}
